import * as ex from "excalibur";
import { createBuildingTileSprite, BUILDING_ANCHOR_Y, } from "../graphics/BuildingAtlas";
import { createLandscapeSprite, BRIDGE_SPRITE, BRIDGE_ANCHOR_Y, } from "../graphics/LandscapeAtlas";
import { showBuildingTooltip, hideBuildingTooltip } from "../ui/BuildingTooltip";
import { hideBuildingInfoOverlay } from "../ui/BuildingInfoOverlay";
import { isMobile } from "../ui/platform";
import { isAccessibilityMode, onAccessibilityModeChange, } from "../ui/accessibility";
import { setCursor, isPlacementActive } from "../ui/cursor";
// Grayscale material for manually-created ('source: manual') company buildings — a global,
// sysadmin-toggled look. This is a per-actor GraphicsComponent property (see onInitialize below),
// NOT a change to the shared cached GraphicsGroup from getGraphic(), which every 'company' actor
// reuses — mutating that group's own tint/opacity would gray out every company, not just manual
// ones. A single Material instance is safe to share across many actors' `graphics.material`.
//
// Built lazily (on the first Building.onInitialize, not at module load) because ex.Material's
// constructor throws unless given a real WebGL graphicsContext — this module is imported
// unconditionally from main.tsx (via game.ts) even before login/engine creation, so constructing
// it eagerly here would crash the entire app on the login screen.
let grayscaleMaterial = null;
function ensureGrayscaleMaterial(engine) {
    if (!grayscaleMaterial) {
        grayscaleMaterial = new ex.Material({
            name: "grayscale",
            graphicsContext: engine.graphicsContext,
            fragmentSource: `#version 300 es
        precision mediump float;
        uniform sampler2D u_graphic;
        in vec2 v_uv;
        out vec4 fragColor;
        void main() {
          vec4 color = texture(u_graphic, v_uv);
          // Excalibur's graphic colors are pre-multiplied by alpha, so a luminance dot product of
          // the premultiplied RGB already yields the correctly-premultiplied gray value — alpha
          // itself is passed through unchanged.
          float gray = dot(color.rgb, vec3(0.299, 0.587, 0.114));
          fragColor = vec4(vec3(gray), color.a);
        }
      `,
        });
    }
    return grayscaleMaterial;
}
let grayscaleEnabled = false;
const liveInstances = new Set();
// Called once per MapState push (GameScene.syncMapState) with the global sysadmin toggle —
// applies live to every currently-rendered Building instance, not just newly-created ones.
export function setGrayscaleEnabled(enabled) {
    if (enabled === grayscaleEnabled)
        return;
    grayscaleEnabled = enabled;
    for (const b of liveInstances)
        b.applyMaterial();
}
export class Building extends ex.Actor {
    company;
    currentUserId;
    canDestroy;
    // Per-variant shared graphic cache — key: 'townhall' | 'company' | 'special:<type>'
    static graphicCache = new Map();
    // Each Building is a separate top-level actor, so moving the pointer straight from one
    // building onto another fires both the old one's 'pointerleave' and the new one's
    // 'pointerenter' in the same dispatch pass — in array order, not time order, so 'leave' can
    // land after 'enter' and clobber the cursor/tooltip the new building just set. Tracking whose
    // hover is currently "owned" lets a leave that arrives late recognize it's stale and no-op.
    static hoveredId = null;
    // Turning accessibility mode on while already hovering a building would otherwise strand its
    // tooltip on screen — 'pointermove' now suppresses showing it in accessibility mode but won't
    // fire again to hide the one already showing. Cursor/hoveredId stay untouched: the mouse is
    // still genuinely over the building, so the pointer-cursor affordance should remain correct.
    static hideTooltipIfHovering() {
        if (Building.hoveredId !== null)
            hideBuildingTooltip();
    }
    constructor(tile, company, currentUserId, canDestroy = false) {
        // Building sprites (64×55) have their diamond center at y = TILE_HEIGHT/2 = 16.
        // BUILDING_ANCHOR_Y = 16/55 places that point at actor.pos = tile.center. The bridge is a
        // flat ground overlay (same crop/front-wall convention as terrain tiles, not a building
        // cube), so it uses LandscapeAtlas's ground-line anchor instead — see BRIDGE_ANCHOR_Y.
        const isBridge = company.kind === "special" && company.specialType === "bridge";
        super({
            pos: tile.center,
            anchor: ex.vec(0.5, isBridge ? BRIDGE_ANCHOR_Y : BUILDING_ANCHOR_Y),
            name: `building-${tile.x}-${tile.y}`,
        });
        this.company = company;
        this.currentUserId = currentUserId;
        this.canDestroy = canDestroy;
        const tileTransform = tile.get(ex.TransformComponent);
        this.transform.z = tileTransform.pos.y;
        this.pointer.useGraphicsBounds = false;
        this.pointer.useColliderShape = false;
    }
    applyMaterial() {
        this.graphics.material =
            grayscaleEnabled && grayscaleMaterial && this.company.source === "manual"
                ? grayscaleMaterial
                : null;
    }
    onInitialize(engine) {
        this.graphics.use(Building.getGraphic(this.company));
        ensureGrayscaleMaterial(engine);
        liveInstances.add(this);
        this.applyMaterial();
        // Derive hit area from the graphic's actual bounds (works for any sprite),
        // inset 5% per edge to avoid transparent sprite corners.
        const b = this.graphics.localBounds;
        const ix = b.width * 0.05;
        const iy = b.height * 0.05;
        this.pointer.localBounds = new ex.BoundingBox({
            left: b.left + ix,
            top: b.top + iy,
            right: b.right - ix,
            bottom: b.bottom - iy,
        });
        // Desktop uses hover (pointerenter/move/leave) to drive the cursor + tooltip; touch devices
        // have no usable hover state at all, so 'pointerenter'/'move'/'leave' are skipped entirely
        // via isMobile() below — on touch, 'pointerenter' fires on touchstart with no matching
        // 'leave' until the next tap elsewhere, which would strand the cursor/hoveredId forever.
        // Accessibility mode still has real (mouse) hover pairing, so it keeps setting the cursor —
        // that's the affordance that the building is clickable — but suppresses the tooltip content,
        // replaced by tap-to-overlay (GameScene wires the tap handler to BuildingInfoOverlay).
        // Checking per-event (instead of deciding once here at init) also lets accessibility mode be
        // toggled live from the profile modal without needing to recreate every building actor.
        const company = this.company;
        const currentUserId = this.currentUserId;
        const hoverCursor = this.canDestroy ? "shovel" : "action";
        this.on("pointerenter", (evt) => {
            if (isMobile())
                return;
            Building.hoveredId = company.id;
            // While placing a special building, the tile-hover preview (hammer/disabled) owns the
            // cursor — a building's own action/destroy cursor would be misleading here, since
            // clicking doesn't run that action while placement is active (see GameScene).
            if (!isPlacementActive())
                setCursor(hoverCursor);
            if (!isAccessibilityMode())
                showBuildingTooltip(evt.screenPos.x, evt.screenPos.y, company, currentUserId);
        });
        this.on("pointermove", (evt) => {
            if (isMobile() || isAccessibilityMode())
                return;
            showBuildingTooltip(evt.screenPos.x, evt.screenPos.y, company, currentUserId);
        });
        this.on("pointerleave", () => {
            if (isMobile())
                return;
            // Only reset if we're still the building marked as hovered — if another building's
            // 'enter' already ran (see the hoveredId comment above), this leave is stale and
            // resetting now would stomp that building's cursor/tooltip back to nothing.
            if (Building.hoveredId !== company.id)
                return;
            Building.hoveredId = null;
            if (!isPlacementActive())
                setCursor("idle");
            hideBuildingTooltip();
        });
    }
    // Clean up hover/tap state when killed via SSE (pointerleave never fires for killed actors).
    // Only touch shared state if we're still the hovered building — see the hoveredId comment.
    onPreKill() {
        liveInstances.delete(this);
        hideBuildingInfoOverlay();
        if (Building.hoveredId !== this.company.id)
            return;
        Building.hoveredId = null;
        hideBuildingTooltip();
        if (!isPlacementActive())
            setCursor("idle");
    }
    static getGraphic(building) {
        const key = building.kind === "special"
            ? `special:${building.specialType ?? ""}`
            : building.kind;
        const cached = Building.graphicCache.get(key);
        if (cached)
            return cached;
        let group;
        if (building.kind === "townhall") {
            const base = createBuildingTileSprite("buildingTiles_052");
            group = new ex.GraphicsGroup({
                members: [{ graphic: base, offset: ex.vec(0, 0) }],
            });
        }
        else if (building.kind === "company") {
            const base = createBuildingTileSprite("buildingTiles_055");
            const roof = createBuildingTileSprite("buildingTiles_074");
            group = new ex.GraphicsGroup({
                members: [
                    { graphic: base, offset: ex.vec(0, 0) },
                    { graphic: roof, offset: ex.vec(0, 0) },
                ],
            });
        }
        else {
            group = Building.buildSpecialGraphic(building.specialType);
        }
        Building.graphicCache.set(key, group);
        return group;
    }
    static buildSpecialGraphic(specialType) {
        switch (specialType) {
            case "bastei": {
                const base = createBuildingTileSprite("buildingTiles_007");
                const top = createBuildingTileSprite("buildingTiles_084");
                return new ex.GraphicsGroup({
                    members: [
                        { graphic: base, offset: ex.vec(0, 0) },
                        { graphic: top, offset: ex.vec(0, 0) },
                    ],
                });
            }
            case "police": {
                const base = createBuildingTileSprite("buildingTiles_016");
                const top = createBuildingTileSprite("buildingTiles_097");
                return new ex.GraphicsGroup({
                    members: [
                        { graphic: base, offset: ex.vec(0, 0) },
                        { graphic: top, offset: ex.vec(0, 0) },
                    ],
                });
            }
            case "bridge": {
                const deck = createLandscapeSprite(BRIDGE_SPRITE);
                return new ex.GraphicsGroup({
                    members: [{ graphic: deck, offset: ex.vec(0, 0) }],
                });
            }
            case "park":
            default: {
                const base = createBuildingTileSprite("buildingTiles_050");
                return new ex.GraphicsGroup({
                    members: [{ graphic: base, offset: ex.vec(0, 0) }],
                });
            }
        }
    }
}
onAccessibilityModeChange((enabled) => {
    if (enabled)
        Building.hideTooltipIfHovering();
});
